import React from 'react'
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  SwipeableDrawer,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { SORT_OPTIONS } from './utils'
import { useQuery } from '@tanstack/react-query'
import campaignApi from '../../../services/campaignApi'

export interface MobileFilterDrawerProps {
  open: boolean
  onOpen: () => void
  onClose: () => void
  activeFilterCount: number
  handleResetMobileFilters: () => void
  dynamicFilters: Record<string, string[]>
  toggleFilterOption: (column: string, value: string) => void
  clearColumnFilter: (column: string) => void
  currentSortField: string
  setSortModel: (model: any[]) => void
  handleSelectSortField: (field: string) => void
  currentSortOrder: 'asc' | 'desc'
  handleSelectSortOrder: (order: 'asc' | 'desc') => void
  isLoading: boolean
  totalMessages: number
  campaignId: string
}

export const MobileFilterDrawer: React.FC<MobileFilterDrawerProps> = ({
  open,
  onOpen,
  onClose,
  activeFilterCount,
  handleResetMobileFilters,
  dynamicFilters,
  toggleFilterOption,
  clearColumnFilter,
  currentSortField,
  setSortModel,
  handleSelectSortField,
  currentSortOrder,
  handleSelectSortOrder,
  isLoading,
  totalMessages,
  campaignId,
}) => {
  const { data: statusOptionsData } = useQuery({
    queryKey: ['campaignMessagesFilters', campaignId, 'status'],
    queryFn: () => campaignApi.getCampaignMessagesStatsFilters(campaignId, 'status', 20, 0),
    enabled: open && !!campaignId,
    staleTime: 15 * 1000, // 15 seconds to match desktop TTL
  })

  // Create the dynamic array, adding __empty__ and all
  const dynamicStatuses = React.useMemo(() => {
    const items = [...(statusOptionsData?.items || [])]
    if (statusOptionsData?.has_empty_values) {
      items.unshift('__empty__')
    }
    return [
      { value: 'all', label: 'All' },
      ...items.map(status => ({
        value: status,
        label: status === '__empty__' ? 'No Status' : status.charAt(0).toUpperCase() + status.slice(1)
      }))
    ]
  }, [statusOptionsData])

  const selectedStatuses = dynamicFilters['status'] || []

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      disableSwipeToOpen
      swipeAreaWidth={30}
      PaperProps={{
        sx: {
          borderRadius: '16px 16px 0 0',
          maxHeight: '85vh',
          overflowY: 'auto',
        },
      }}
    >
      {/* Grabber Handle */}
      <Box
        onClick={onClose}
        sx={{
          pt: 1.5,
          pb: 1,
          display: 'flex',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: 'action.disabled',
          }}
        />
      </Box>

      {/* Drawer Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Filter & Sort
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeFilterCount > 0 && (
            <Button
              size="small"
              onClick={handleResetMobileFilters}
              sx={{ fontSize: '0.75rem', textTransform: 'none', minWidth: 'auto' }}
            >
              Reset all
            </Button>
          )}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'action.active' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Drawer Content */}
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Status Section */}
        <Box>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}
          >
            Message Status
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {dynamicStatuses.map((opt) => {
              const isSelected =
                opt.value === 'all'
                  ? selectedStatuses.length === 0
                  : selectedStatuses.includes(opt.value)

              return (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  clickable
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() =>
                    opt.value === 'all'
                      ? clearColumnFilter('status')
                      : toggleFilterOption('status', opt.value)
                  }
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: '0.8125rem',
                    py: 1.5,
                    px: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.15s ease-in-out',
                    ...(isSelected
                      ? {
                        boxShadow: '0 1px 3px rgba(59, 130, 246, 0.25)',
                      }
                      : {
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }),
                  }}
                />
              )
            })}
          </Box>
        </Box>

        <Divider />

        {/* Sort By Section */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Sort By
            </Typography>
            {currentSortField && (
              <Button
                size="small"
                onClick={() => setSortModel([])}
                sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto', textTransform: 'none' }}
              >
                Clear sort
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {SORT_OPTIONS.map((opt) => {
              const isSelected = currentSortField === opt.value

              return (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  clickable
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() => handleSelectSortField(opt.value)}
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: '0.8125rem',
                    py: 1.5,
                    px: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.15s ease-in-out',
                    ...(isSelected
                      ? {
                        boxShadow: '0 1px 3px rgba(59, 130, 246, 0.25)',
                      }
                      : {
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }),
                  }}
                />
              )
            })}
          </Box>
        </Box>

        {/* Sort Direction */}
        <Box
          sx={{
            pointerEvents: currentSortField ? 'auto' : 'none',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color={currentSortField ? "text.secondary" : "text.disabled"}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}
          >
            Sort Direction {!currentSortField && '(Select sort column above)'}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={currentSortOrder}
            onChange={(_, val) => val && handleSelectSortOrder(val)}
            fullWidth
            disabled={!currentSortField}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8125rem',
                py: 0.75,
              }
            }}
          >
            <ToggleButton value="desc">
              {currentSortField?.endsWith('_at') ? 'Newest First' : 'Z to A'}
            </ToggleButton>
            <ToggleButton value="asc">
              {currentSortField?.endsWith('_at') ? 'Oldest First' : 'A to Z'}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Footer Action */}
        <Box sx={{ pt: 1 }}>
          <Button
            variant="contained"
            fullWidth
            disabled={isLoading}
            onClick={onClose}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              py: 1.25,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 1.5,
            }}
          >
            {isLoading
              ? 'Updating...'
              : `Show ${totalMessages} ${totalMessages === 1 ? 'Result' : 'Results'}`}
          </Button>
        </Box>
      </Box>
    </SwipeableDrawer>
  )
}
